import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useI18n } from '@/i18n/use-i18n';
import { normalizeLocale } from '@/i18n/locale';
import { authService, type AuthSession, userService, type CurrentUserInfo } from '@/services';
import './auth-guard.css';

type AuthGuardProps = {
  children: ReactNode;
};

export function AuthGuard({ children }: AuthGuardProps) {
  const { locale, setLocale, strings } = useI18n();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const { data: currentUser } = useQuery<CurrentUserInfo>({
    queryKey: ['current-user'],
    queryFn: userService.getCurrentUser,
    enabled: Boolean(session),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      try {
        const currentSession = await authService.getSession();
        if (!isMounted) {
          return;
        }
        setSession(currentSession);
      } finally {
        if (isMounted) {
          setIsCheckingSession(false);
        }
      }
    };

    loadSession();

    const unsubscribe = authService.onAuthStateChange((newSession) => {
      setSession(newSession);
      setIsCheckingSession(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const storedLocale = typeof currentUser?.locale === 'string' ? currentUser.locale : null;
    const normalized = normalizeLocale(storedLocale);
    if (normalized && normalized !== locale) {
      setLocale(normalized);
    }
  }, [currentUser, locale, setLocale]);

  if (isCheckingSession) {
    return (
      <div className="auth-guard__loader" role="status" aria-live="polite">
        {strings.authGuard.validating}
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
